/**
 * Operations: a month grid of hours. A cell opens that person’s shifts for
 * that day. Each shift ends after it starts, and one person’s shifts don’t overlap.
 */
import { useEffect, useRef, useState, type MouseEvent as ReactMouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight, Plus, Trash2 } from 'lucide-react';
import type { Database } from '@cafe-pos/supabase';
import { resolveAppLocale } from '../locale.js';
import { supabase } from '../supabase.js';
import { Button, Callout, IconButton, Modal, PageHeader, chartColor } from '../ui/index.js';

type StaffRow = Database['public']['Tables']['staff']['Row'];

interface DayLine {
  key: string;
  staffId: string;
  start: string;
  end: string;
}

interface Span {
  staffId: string;
  day: string;
  start: string;
  end: string;
  off?: boolean;
}

interface ShiftSlot {
  start: string;
  end: string;
  off?: boolean;
}

interface CellRef {
  staffId: string;
  day: string;
}

function pad(n: number): string {
  return String(n).padStart(2, '0');
}

function isoDate(d: Date): string {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function dateParts(value: string): [number, number, number] {
  return [Number(value.slice(0, 4)), Number(value.slice(5, 7)), Number(value.slice(8, 10))];
}

function dayBounds(date: string): [string, string] {
  const [y, m, d] = dateParts(date);
  return [
    new Date(y, m - 1, d, 0, 0, 0, 0).toISOString(),
    new Date(y, m - 1, d, 23, 59, 59, 999).toISOString(),
  ];
}

function atTime(date: string, time: string): string {
  const [y, m, d] = dateParts(date);
  const hh = Number(time.slice(0, 2));
  const mm = Number(time.slice(3, 5));
  return new Date(y, m - 1, d, hh, mm, 0, 0).toISOString();
}

function minutesOf(time: string): number {
  return Number(time.slice(0, 2)) * 60 + Number(time.slice(3, 5));
}

/** Monday of the week that contains `date`. Weeks start on Monday. */
function mondayOf(date: Date): string {
  const day = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const dow = day.getDay();
  day.setDate(day.getDate() + (dow === 0 ? -6 : 1 - dow));
  return isoDate(day);
}

function slotProblem(
  slots: { start: string; end: string }[],
): 'staff.sheetIncomplete' | 'staff.sheetOrder' | 'staff.sheetOverlap' | null {
  const filled = slots.filter((slot) => slot.start || slot.end);
  if (filled.some((slot) => !slot.start || !slot.end)) return 'staff.sheetIncomplete';
  const ranges = filled.map((slot) => ({ start: minutesOf(slot.start), end: minutesOf(slot.end) }));
  if (ranges.some((range) => range.end <= range.start)) return 'staff.sheetOrder';
  ranges.sort((a, b) => a.start - b.start || a.end - b.end);
  for (let i = 1; i < ranges.length; i += 1) {
    const prev = ranges[i - 1];
    const next = ranges[i];
    if (prev && next && next.start < prev.end) return 'staff.sheetOverlap';
  }
  return null;
}

function timePart(iso: string): string {
  const d = new Date(iso);
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

let lineSeq = 0;
function lineKey(): string {
  lineSeq += 1;
  return `line-${lineSeq}`;
}

function spanMinutes(span: { start: string; end: string; off?: boolean }): number {
  if (span.off || !span.start || !span.end) return 0;
  return Math.max(0, minutesOf(span.end) - minutesOf(span.start));
}

function entryForSlot(clientId: string, staffId: string, day: string, slot: ShiftSlot) {
  if (slot.off) {
    return {
      client_id: clientId,
      staff_id: staffId,
      clock_in: atTime(day, '00:00'),
      clock_out: atTime(day, '00:01'),
      break_minutes: 0,
      note: 'off',
    };
  }
  return {
    client_id: clientId,
    staff_id: staffId,
    clock_in: atTime(day, slot.start),
    clock_out: atTime(day, slot.end),
    break_minutes: 0,
    note: null as string | null,
  };
}

function slotReady(line: Pick<DayLine, 'start' | 'end'>): boolean {
  return Boolean(line.start && line.end && minutesOf(line.end) > minutesOf(line.start));
}

function shiftMonth(date: string, delta: number): string {
  const [y, m, d] = dateParts(date);
  const next = new Date(y, m - 1 + delta, 1);
  const dim = new Date(next.getFullYear(), next.getMonth() + 1, 0).getDate();
  next.setDate(Math.min(d, dim));
  return isoDate(next);
}

function daysOfMonth(date: string): string[] {
  const [y, m] = dateParts(date);
  const count = new Date(y, m, 0).getDate();
  const days: string[] = [];
  for (let d = 1; d <= count; d += 1) days.push(isoDate(new Date(y, m - 1, d)));
  return days;
}

function dayClass(ymd: string, today: string): string {
  const dow = new Date(`${ymd}T12:00:00`).getDay();
  return [dow === 0 || dow === 6 ? 'is-weekend' : '', ymd === today ? 'is-today' : ''].filter(Boolean).join(' ');
}

function clock(mins: number): string {
  return `${pad(Math.floor(mins / 60))}:${pad(mins % 60)}`;
}

interface DayBar {
  staffId: string;
  name: string;
  start: number;
  end: number;
}

interface OverlapBand {
  start: number;
  end: number;
  names: string[];
}

function dayCoverage(
  spans: readonly Span[],
  people: readonly { id: string; name: string }[],
  day: string,
): { bars: DayBar[]; bands: OverlapBand[] } {
  const nameOf = new Map(people.map((person) => [person.id, person.name]));
  const bars: DayBar[] = [];
  for (const span of spans) {
    if (span.day !== day || !slotReady(span)) continue;
    const name = nameOf.get(span.staffId);
    if (!name) continue;
    bars.push({ staffId: span.staffId, name, start: minutesOf(span.start), end: minutesOf(span.end) });
  }
  bars.sort((a, b) => a.start - b.start || a.name.localeCompare(b.name));
  const marks = [...new Set(bars.flatMap((bar) => [bar.start, bar.end]))].sort((a, b) => a - b);
  const bands: OverlapBand[] = [];
  for (let i = 0; i < marks.length - 1; i += 1) {
    const start = marks[i];
    const end = marks[i + 1];
    if (start == null || end == null || end <= start) continue;
    const names = [
      ...new Set(bars.filter((bar) => bar.start <= start && bar.end >= end).map((bar) => bar.name)),
    ].sort((a, b) => a.localeCompare(b));
    if (names.length < 2) continue;
    const prev = bands[bands.length - 1];
    if (prev && prev.end === start && prev.names.join() === names.join()) prev.end = end;
    else bands.push({ start, end, names });
  }
  return { bars, bands };
}

function selectionBox(
  ids: readonly string[],
  days: readonly string[],
  from: { staffId: string; day: string },
  to: { staffId: string; day: string },
): { rowStart: number; rowEnd: number; colStart: number; colEnd: number } | null {
  const rowA = ids.indexOf(from.staffId);
  const rowB = ids.indexOf(to.staffId);
  const colA = days.indexOf(from.day);
  const colB = days.indexOf(to.day);
  if (rowA < 0 || rowB < 0 || colA < 0 || colB < 0) return null;
  return {
    rowStart: Math.min(rowA, rowB),
    rowEnd: Math.max(rowA, rowB),
    colStart: Math.min(colA, colB),
    colEnd: Math.max(colA, colB),
  };
}

function cellKey(cell: CellRef): string {
  return `${cell.staffId}|${cell.day}`;
}

function parseCell(key: string): CellRef | null {
  const splitAt = key.indexOf('|');
  if (splitAt < 0) return null;
  const staffId = key.slice(0, splitAt);
  const day = key.slice(splitAt + 1);
  if (!staffId || !day) return null;
  return { staffId, day };
}

function cellsInBox(
  ids: readonly string[],
  days: readonly string[],
  from: CellRef,
  to: CellRef,
): CellRef[] {
  const box = selectionBox(ids, days, from, to);
  if (!box) return [];
  const cells: CellRef[] = [];
  for (let row = box.rowStart; row <= box.rowEnd; row += 1) {
    const staffId = ids[row];
    if (!staffId) continue;
    for (let col = box.colStart; col <= box.colEnd; col += 1) {
      const day = days[col];
      if (day) cells.push({ staffId, day });
    }
  }
  return cells;
}

function selectionShadow(top: boolean, right: boolean, bottom: boolean, left: boolean): string {
  const edges: string[] = [];
  if (top) edges.push('inset 0 2px 0 var(--info)');
  if (bottom) edges.push('inset 0 -2px 0 var(--info)');
  if (left) edges.push('inset 2px 0 0 var(--info)');
  if (right) edges.push('inset -2px 0 0 var(--info)');
  return edges.join(', ');
}

export function TimesheetView({ clientId }: { clientId: string | null }) {
  const { t, i18n } = useTranslation();
  const locale = resolveAppLocale(i18n.language);

  function durationLabel(minutes: number): string {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0 && mins > 0) return t('staff.duration', { hours, mins });
    if (hours > 0) return t('staff.durationHours', { hours });
    return t('staff.durationMins', { mins });
  }
  const monthRef = useRef<HTMLDivElement>(null);
  const alignedMonth = useRef<string | null>(null);

  const [staff, setStaff] = useState<StaffRow[]>([]);
  const [cursor, setCursor] = useState(() => isoDate(new Date()));
  const [spans, setSpans] = useState<Span[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [edit, setEdit] = useState<{ staffId: string; day: string } | null>(null);
  const [editLines, setEditLines] = useState<DayLine[]>([]);
  const [editOff, setEditOff] = useState(false);
  const [picked, setPicked] = useState<string[]>([]);
  const [anchor, setAnchor] = useState<CellRef | null>(null);
  const [active, setActive] = useState<CellRef | null>(null);
  const [overlapDay, setOverlapDay] = useState<string | null>(null);
  const [dragTo, setDragTo] = useState<{ staffId: string; day: string } | null>(null);
  const [dragFrom, setDragFrom] = useState<CellRef | null>(null);
  const spansRef = useRef(spans);
  const dragToRef = useRef<{ staffId: string; day: string } | null>(null);
  const pickedRef = useRef(picked);
  const savingRef = useRef(false);
  const editRef = useRef(edit);
  const overlapRef = useRef(overlapDay);
  const clearRef = useRef<() => void>(() => {});
  const suppressClick = useRef(false);
  spansRef.current = spans;
  pickedRef.current = picked;
  savingRef.current = saving;
  editRef.current = edit;
  overlapRef.current = overlapDay;

  const monthId = cursor.slice(0, 7);

  useEffect(() => {
    if (!supabase || !clientId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    const [y, m] = dateParts(`${monthId}-01`);
    const startIso = new Date(y, m - 1, 1, 0, 0, 0, 0).toISOString();
    const endIso = new Date(y, m, 0, 23, 59, 59, 999).toISOString();
    void (async () => {
      const [staffRes, entryRes] = await Promise.all([
        supabase
          .from('staff')
          .select('*')
          .eq('client_id', clientId)
          .order('sort_order', { ascending: true })
          .order('name', { ascending: true }),
        supabase
          .from('time_entries')
          .select('staff_id, clock_in, clock_out, note')
          .eq('client_id', clientId)
          .gte('clock_in', startIso)
          .lte('clock_in', endIso)
          .order('clock_in', { ascending: true }),
      ]);
      if (cancelled) return;
      const err = staffRes.error ?? entryRes.error;
      if (err) {
        setError(err.message);
        setLoading(false);
        return;
      }
      setStaff(staffRes.data ?? []);
      setSpans(
        (entryRes.data ?? []).map((row) => {
          const off = row.note === 'off';
          return {
            staffId: row.staff_id,
            day: isoDate(new Date(row.clock_in)),
            start: off ? '' : timePart(row.clock_in),
            end: off || !row.clock_out ? '' : timePart(row.clock_out),
            off,
          };
        }),
      );
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [clientId, monthId]);

  useEffect(() => {
    setPicked([]);
    setAnchor(null);
    setActive(null);
  }, [monthId]);

  useEffect(() => {
    function onKey(event: KeyboardEvent) {
      if (event.key !== 'Delete' && event.key !== 'Backspace') return;
      const target = event.target;
      if (target instanceof HTMLElement && target.closest('input, textarea, select, [contenteditable="true"]')) return;
      if (editRef.current || overlapRef.current || savingRef.current) return;
      if (pickedRef.current.length === 0) return;
      event.preventDefault();
      clearRef.current();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  function replaceSpans(staffId: string, day: string, slots: ShiftSlot[]) {
    setSpans((current) => [
      ...current.filter((span) => !(span.staffId === staffId && span.day === day)),
      ...slots.map((slot) => ({
        staffId,
        day,
        start: slot.off ? '' : slot.start,
        end: slot.off ? '' : slot.end,
        off: slot.off,
      })),
    ]);
  }

  async function writePersonDay(
    staffId: string,
    day: string,
    slots: ShiftSlot[],
  ): Promise<string | null> {
    if (!supabase || !clientId) return null;
    const [startIso, endIso] = dayBounds(day);
    const { data: existing, error: loadErr } = await supabase
      .from('time_entries')
      .select('id')
      .eq('client_id', clientId)
      .eq('staff_id', staffId)
      .gte('clock_in', startIso)
      .lte('clock_in', endIso);
    if (loadErr) return loadErr.message;
    const ids = (existing ?? []).map((entry) => entry.id);
    if (ids.length > 0) {
      const { error: delErr } = await supabase.from('time_entries').delete().in('id', ids);
      if (delErr) return delErr.message;
    }
    if (slots.length === 0) return null;
    const rows = (slots.some((slot) => slot.off) ? slots.filter((slot) => slot.off).slice(0, 1) : slots).map(
      (slot) => entryForSlot(clientId, staffId, day, slot),
    );
    const { error: insErr } = await supabase.from('time_entries').insert(rows);
    return insErr?.message ?? null;
  }

  async function writeMany(
    targets: { staffId: string; day: string }[],
    slots: ShiftSlot[],
  ): Promise<string | null> {
    if (!supabase || !clientId || targets.length === 0) return null;
    const days = [...targets.map((target) => target.day)].sort();
    const first = days[0];
    const last = days[days.length - 1];
    if (!first || !last) return null;
    const [startIso] = dayBounds(first);
    const [, endIso] = dayBounds(last);
    const staffIds = [...new Set(targets.map((target) => target.staffId))];
    const wanted = new Set(targets.map((target) => `${target.staffId}|${target.day}`));
    const { data: existing, error: loadErr } = await supabase
      .from('time_entries')
      .select('id, staff_id, clock_in')
      .eq('client_id', clientId)
      .in('staff_id', staffIds)
      .gte('clock_in', startIso)
      .lte('clock_in', endIso);
    if (loadErr) return loadErr.message;
    const ids = (existing ?? [])
      .filter((row) => wanted.has(`${row.staff_id}|${isoDate(new Date(row.clock_in))}`))
      .map((row) => row.id);
    for (let i = 0; i < ids.length; i += 100) {
      const { error: delErr } = await supabase.from('time_entries').delete().in('id', ids.slice(i, i + 100));
      if (delErr) return delErr.message;
    }
    if (slots.length === 0) return null;
    const written = slots.some((slot) => slot.off) ? slots.filter((slot) => slot.off).slice(0, 1) : slots;
    const rows = targets.flatMap((target) =>
      written.map((slot) => entryForSlot(clientId, target.staffId, target.day, slot)),
    );
    for (let i = 0; i < rows.length; i += 200) {
      const { error: insErr } = await supabase.from('time_entries').insert(rows.slice(i, i + 200));
      if (insErr) return insErr.message;
    }
    return null;
  }

  function openCell(staffId: string, day: string) {
    const existing = spans.filter((span) => span.staffId === staffId && span.day === day);
    const off = existing.some((span) => span.off);
    const shifts = existing.filter((span) => !span.off);
    setEditOff(off);
    setEditLines(
      shifts.length > 0
        ? shifts.map((span) => ({ key: lineKey(), staffId, start: span.start, end: span.end }))
        : [{ key: lineKey(), staffId, start: '', end: '' }],
    );
    setEdit({ staffId, day });
    setError(null);
  }

  function patchLine(key: string, patch: Partial<Pick<DayLine, 'start' | 'end'>>) {
    setEditLines((current) => current.map((item) => (item.key === key ? { ...item, ...patch } : item)));
  }

  function addShift() {
    if (!edit) return;
    setEditLines((current) => [...current, { key: lineKey(), staffId: edit.staffId, start: '', end: '' }]);
  }

  function clearLine(key: string) {
    setEditLines((current) => {
      if (current.length <= 1) {
        return current.map((item) => (item.key === key ? { ...item, start: '', end: '' } : item));
      }
      return current.filter((item) => item.key !== key);
    });
  }

  function pickCell(event: ReactMouseEvent, staffId: string, day: string) {
    const cell = { staffId, day };
    const key = cellKey(cell);
    if (event.shiftKey && anchor) {
      setPicked(cellsInBox(peopleIds, monthDays, anchor, cell).map(cellKey));
      setActive(cell);
      return;
    }
    if (event.metaKey || event.ctrlKey) {
      const next = picked.includes(key) ? picked.filter((item) => item !== key) : [...picked, key];
      setPicked(next);
      const stay = next.includes(key) ? cell : parseCell(next[next.length - 1] ?? '');
      setAnchor(stay);
      setActive(stay);
      return;
    }
    setPicked([key]);
    setAnchor(cell);
    setActive(cell);
  }

  function startFill(event: ReactMouseEvent, staffId: string, day: string) {
    event.preventDefault();
    event.stopPropagation();
    const origin = { staffId, day };
    dragToRef.current = origin;
    setDragFrom(origin);
    setDragTo(origin);
    const days = monthDays;
    const ids = people.map((person) => person.id);
    function move(moveEvent: MouseEvent) {
      const hit = document.elementFromPoint(moveEvent.clientX, moveEvent.clientY);
      const cell = hit instanceof Element ? hit.closest('[data-staff][data-day]') : null;
      if (!(cell instanceof HTMLElement) || !cell.dataset.staff || !cell.dataset.day) return;
      const next = { staffId: cell.dataset.staff, day: cell.dataset.day };
      dragToRef.current = next;
      setDragTo(next);
    }
    function up() {
      window.removeEventListener('mousemove', move);
      window.removeEventListener('mouseup', up);
      const to = dragToRef.current;
      dragToRef.current = null;
      setDragFrom(null);
      setDragTo(null);
      if (!to || (to.staffId === staffId && to.day === day)) return;
      suppressClick.current = true;
      void fillRect(staffId, day, to, days, ids);
    }
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
  }

  async function fillRect(
    staffId: string,
    fromDay: string,
    to: { staffId: string; day: string },
    days: string[],
    ids: string[],
  ) {
    const sourceSpans = spansRef.current.filter((span) => span.staffId === staffId && span.day === fromDay);
    const sourceOff = sourceSpans.some((span) => span.off);
    const slots: ShiftSlot[] = sourceOff
      ? [{ start: '', end: '', off: true }]
      : sourceSpans.filter((span) => slotReady(span)).map((span) => ({ start: span.start, end: span.end }));
    if (sourceSpans.length > 0 && slots.length === 0) return;
    const box = selectionBox(ids, days, { staffId, day: fromDay }, to);
    if (!box) return;
    const targets: { staffId: string; day: string }[] = [];
    for (let row = box.rowStart; row <= box.rowEnd; row += 1) {
      const targetId = ids[row];
      if (!targetId) continue;
      for (let col = box.colStart; col <= box.colEnd; col += 1) {
        const targetDay = days[col];
        if (!targetDay || (targetId === staffId && targetDay === fromDay)) continue;
        targets.push({ staffId: targetId, day: targetDay });
      }
    }
    if (targets.length === 0) return;
    const wanted = new Set(targets.map((target) => `${target.staffId}|${target.day}`));
    setSaving(true);
    setError(null);
    const message = await writeMany(targets, slots);
    if (message) {
      setError(message);
      setSaving(false);
      return;
    }
    setSpans((current) => {
      const kept = current.filter((span) => !wanted.has(`${span.staffId}|${span.day}`));
      if (slots.length === 0) return kept;
      return [
        ...kept,
        ...targets.flatMap((target) =>
          slots.map((slot) => ({
            staffId: target.staffId,
            day: target.day,
            start: slot.off ? '' : slot.start,
            end: slot.off ? '' : slot.end,
            off: slot.off,
          })),
        ),
      ];
    });
    setSaving(false);
  }

  async function clearPicked() {
    if (savingRef.current) return;
    const targets = pickedRef.current
      .map(parseCell)
      .filter((cell): cell is CellRef => cell != null)
      .filter((cell) =>
        spansRef.current.some((span) => span.staffId === cell.staffId && span.day === cell.day),
      );
    if (targets.length === 0) return;
    const wanted = new Set(targets.map((target) => cellKey(target)));
    setSaving(true);
    setError(null);
    const message = await writeMany(targets, []);
    if (message) {
      setError(message);
      setSaving(false);
      return;
    }
    setSpans((current) => current.filter((span) => !wanted.has(cellKey(span))));
    setSaving(false);
  }
  clearRef.current = () => {
    void clearPicked();
  };

  async function saveEdit() {
    if (!edit || !supabase || !clientId) return;
    const filled = editOff ? [] : editLines.filter((line) => line.start || line.end);
    const problem = editOff ? null : slotProblem(filled);
    if (problem) {
      setError(t(problem));
      return;
    }
    setSaving(true);
    setError(null);
    const slots: ShiftSlot[] = editOff
      ? [{ start: '', end: '', off: true }]
      : filled.map((line) => ({ start: line.start, end: line.end }));
    const message = await writePersonDay(edit.staffId, edit.day, slots);
    setSaving(false);
    if (message) {
      setError(message);
      return;
    }
    replaceSpans(edit.staffId, edit.day, slots);
    setEdit(null);
  }

  const today = isoDate(new Date());
  const weekMonday = mondayOf(new Date());

  useEffect(() => {
    if (loading || alignedMonth.current === monthId) return;
    if (weekMonday.slice(0, 7) !== monthId) {
      alignedMonth.current = monthId;
      return;
    }
    const root = monthRef.current;
    const col = root?.querySelector<HTMLElement>(`th[data-day="${weekMonday}"]`);
    const name = root?.querySelector<HTMLElement>('tbody th');
    if (!root || !col) return;
    const nameWidth = name?.getBoundingClientRect().width ?? 0;
    const delta = col.getBoundingClientRect().left - root.getBoundingClientRect().left - nameWidth;
    root.scrollLeft += delta;
    alignedMonth.current = monthId;
  }, [loading, monthId, weekMonday]);
  const monthDays = daysOfMonth(cursor);
  const monthLabel = new Date(`${monthId}-01T12:00:00`).toLocaleDateString(locale, {
    month: 'long',
    year: 'numeric',
  });
  const people = staff.filter(
    (person) =>
      (person.on_timesheet && person.active) || spans.some((span) => span.staffId === person.id),
  );
  const peopleIds = people.map((person) => person.id);
  const visualKeys = new Set(picked);
  if (dragTo && dragFrom) {
    for (const cell of cellsInBox(peopleIds, monthDays, dragFrom, dragTo)) visualKeys.add(cellKey(cell));
  }
  let handleKey: string | null = dragTo ? cellKey(dragTo) : null;
  if (!handleKey) {
    let bestRow = -1;
    let bestCol = -1;
    for (const key of picked) {
      const cell = parseCell(key);
      if (!cell) continue;
      const row = peopleIds.indexOf(cell.staffId);
      const col = monthDays.indexOf(cell.day);
      if (row < 0 || col < 0) continue;
      if (row > bestRow || (row === bestRow && col > bestCol)) {
        bestRow = row;
        bestCol = col;
        handleKey = key;
      }
    }
  }
  const canDelete = picked.some((key) => {
    const cell = parseCell(key);
    return cell != null && spans.some((span) => span.staffId === cell.staffId && span.day === cell.day);
  });
  const editPerson = staff.find((person) => person.id === edit?.staffId) ?? null;
  const editLabel = edit
    ? new Date(`${edit.day}T12:00:00`).toLocaleDateString(locale, {
        weekday: 'short',
        day: 'numeric',
        month: 'short',
      })
    : '';
  const coverage = overlapDay ? dayCoverage(spans, staff, overlapDay) : null;
  const overlapView = coverage ? <OverlapDay coverage={coverage} /> : null;

  return (
    <section className="ui-stack">
      <PageHeader title={t('staff.timesheet')} subtitle={t('staff.monthHint')} />

      {error && !edit && <Callout tone="danger">{error}</Callout>}

      <div className="timesheet-sheet">
        <div className="timesheet-day">
          <IconButton label={t('staff.prevMonth')} onClick={() => setCursor((date) => shiftMonth(date, -1))}>
            <ChevronLeft className="ui-i" />
          </IconButton>
          <input
            type="month"
            value={monthId}
            aria-label={monthLabel}
            onChange={(e) => e.target.value && setCursor(`${e.target.value}-01`)}
          />
          <IconButton label={t('staff.nextMonth')} onClick={() => setCursor((date) => shiftMonth(date, 1))}>
            <ChevronRight className="ui-i" />
          </IconButton>
          <span className="timesheet-day-label">{monthLabel}</span>
          <span className="timesheet-day-spacer" />
          <Button
            variant="secondary"
            icon={<Trash2 className="ui-i" />}
            disabled={!canDelete || saving}
            onClick={() => void clearPicked()}
          >
            {t('common.delete')}
          </Button>
          <Button
            variant="secondary"
            disabled={!(active?.day ?? (today.startsWith(monthId) ? today : ''))}
            onClick={() => {
              const day = active?.day ?? (today.startsWith(monthId) ? today : '');
              if (day) setOverlapDay(day);
            }}
          >
            {t('staff.overlaps')}
          </Button>
        </div>

        {loading ? (
          <div className="empty-state">{t('common.loading')}</div>
        ) : people.length === 0 ? (
          <div className="empty-state">{t('staff.empty')}</div>
        ) : (
          <div className="timesheet-month" ref={monthRef}>
            <table className="timesheet-grid">
              <thead>
                <tr>
                  <th>{t('staff.colName')}</th>
                  {monthDays.map((ymd) => {
                    const label = new Date(`${ymd}T12:00:00`);
                    return (
                      <th key={ymd} data-day={ymd} className={dayClass(ymd, today)}>
                        <button
                          type="button"
                          className="timesheet-dayhead"
                          title={t('staff.showOverlaps')}
                          onClick={() => setOverlapDay(ymd)}
                        >
                          <span className="timesheet-dom">{label.getDate()}</span>
                          <span className="timesheet-dow">
                            {label.toLocaleDateString(locale, { weekday: 'short' })}
                            {dayCoverage(spans, staff, ymd).bands.length > 0 && (
                              <span className="timesheet-overlap-dot" />
                            )}
                          </span>
                        </button>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {people.map((person) => (
                  <tr key={person.id}>
                    <th scope="row">{person.name}</th>
                    {monthDays.map((ymd) => {
                      const daySpans = spans.filter((span) => span.staffId === person.id && span.day === ymd);
                      const isOff = daySpans.some((span) => span.off);
                      const ranges = isOff
                        ? []
                        : daySpans.map((span) => (span.end ? `${span.start}–${span.end}` : `${span.start}–`));
                      const totalMinutes = daySpans.reduce((sum, span) => sum + spanMinutes(span), 0);
                      const when = new Date(`${ymd}T12:00:00`).toLocaleDateString(locale, {
                        day: 'numeric',
                        month: 'short',
                      });
                      const rowIndex = peopleIds.indexOf(person.id);
                      const colIndex = monthDays.indexOf(ymd);
                      const key = cellKey({ staffId: person.id, day: ymd });
                      const inSelection = visualKeys.has(key);
                      const upId = peopleIds[rowIndex - 1];
                      const downId = peopleIds[rowIndex + 1];
                      const leftDay = monthDays[colIndex - 1];
                      const rightDay = monthDays[colIndex + 1];
                      return (
                        <td
                          key={ymd}
                          data-staff={person.id}
                          data-day={ymd}
                          className={[dayClass(ymd, today), inSelection ? 'is-range' : ''].filter(Boolean).join(' ')}
                          style={
                            inSelection
                              ? {
                                  boxShadow: selectionShadow(
                                    upId == null || !visualKeys.has(cellKey({ staffId: upId, day: ymd })),
                                    rightDay == null || !visualKeys.has(cellKey({ staffId: person.id, day: rightDay })),
                                    downId == null || !visualKeys.has(cellKey({ staffId: downId, day: ymd })),
                                    leftDay == null || !visualKeys.has(cellKey({ staffId: person.id, day: leftDay })),
                                  ),
                                }
                              : undefined
                          }
                        >
                          <button
                            type="button"
                            className="timesheet-cell"
                            onClick={(event) => {
                              if (suppressClick.current) {
                                suppressClick.current = false;
                                return;
                              }
                              pickCell(event, person.id, ymd);
                            }}
                            onDoubleClick={() => openCell(person.id, ymd)}
                            aria-label={t('staff.openDay', { name: person.name, date: when })}
                          >
                            {isOff ? (
                              <span className="timesheet-cell-hours">{t('staff.off')}</span>
                            ) : ranges.length === 0 ? (
                              <span className="timesheet-cell-empty">–</span>
                            ) : (
                              <>
                                {totalMinutes > 0 && (
                                  <span className="timesheet-cell-hours">{durationLabel(totalMinutes)}</span>
                                )}
                                {ranges.map((range, index) => (
                                  <span key={`${range}-${index}`}>{range}</span>
                                ))}
                              </>
                            )}
                          </button>
                          {handleKey === key && (
                            <span
                              className="timesheet-fill"
                              title={t('staff.fillDrag')}
                              onMouseDown={(event) => startFill(event, person.id, ymd)}
                            />
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Modal
        open={overlapDay != null}
        onClose={() => setOverlapDay(null)}
        title={t('staff.overlaps')}
        caption={
          overlapDay
            ? new Date(`${overlapDay}T12:00:00`).toLocaleDateString(locale, {
                weekday: 'short',
                day: 'numeric',
                month: 'short',
              })
            : undefined
        }
      >
        {overlapView}
      </Modal>

      <Modal
        open={edit != null}
        onClose={() => {
          if (!saving) setEdit(null);
        }}
        title={editPerson?.name ?? ''}
        caption={editLabel}
        footer={
          <>
            <Button variant="secondary" disabled={saving} onClick={() => setEdit(null)}>
              {t('common.cancel')}
            </Button>
            <Button variant="primary" disabled={saving} onClick={() => void saveEdit()}>
              {saving ? '…' : t('common.save')}
            </Button>
          </>
        }
      >
        {error && edit && <Callout tone="danger">{error}</Callout>}
        <div className="timesheet-editor">
          <label className="timesheet-off">
            <input
              type="checkbox"
              checked={editOff}
              onChange={(e) => setEditOff(e.target.checked)}
            />
            {t('staff.markOff')}
          </label>
          {editOff ? (
            <p className="timesheet-off-hint">{t('staff.offHint')}</p>
          ) : (
          <div className="timesheet-slots">
            {editLines.map((line) => (
              <div className="timesheet-slot" key={line.key}>
                <input
                  type="time"
                  value={line.start}
                  aria-label={t('staff.start')}
                  onChange={(e) => patchLine(line.key, { start: e.target.value })}
                />
                <span className="timesheet-dash" aria-hidden>
                  –
                </span>
                <input
                  type="time"
                  value={line.end}
                  aria-label={t('staff.end')}
                  onChange={(e) => patchLine(line.key, { end: e.target.value })}
                />
                <div className="timesheet-slot-actions">
                  <IconButton label={t('staff.addShift')} onClick={addShift}>
                    <Plus className="ui-i" />
                  </IconButton>
                  <IconButton label={t('common.delete')} danger onClick={() => clearLine(line.key)}>
                    <Trash2 className="ui-i" />
                  </IconButton>
                </div>
              </div>
            ))}
          </div>
          )}
        </div>
      </Modal>
    </section>
  );
}

function OverlapDay({ coverage }: { coverage: { bars: DayBar[]; bands: OverlapBand[] } }) {
  const { t } = useTranslation();
  if (coverage.bars.length === 0) return <p className="muted">{t('staff.overlapEmpty')}</p>;
  const windowStart = Math.min(...coverage.bars.map((bar) => bar.start));
  const windowEnd = Math.max(...coverage.bars.map((bar) => bar.end));
  const span = Math.max(1, windowEnd - windowStart);
  const rows = [...new Map(coverage.bars.map((bar) => [bar.staffId, bar.name])).entries()];
  return (
    <div className="timesheet-cover">
      {rows.map(([staffId, name], index) => (
        <div className="timesheet-cover-row" key={staffId}>
          <span className="timesheet-cover-name">{name}</span>
          <span className="timesheet-cover-track">
            {coverage.bars
              .filter((bar) => bar.staffId === staffId)
              .map((bar) => (
                <span
                  key={`${bar.start}-${bar.end}`}
                  className="timesheet-cover-bar"
                  style={{
                    left: `${((bar.start - windowStart) / span) * 100}%`,
                    width: `${((bar.end - bar.start) / span) * 100}%`,
                    background: chartColor(index),
                  }}
                />
              ))}
          </span>
        </div>
      ))}
      <div className="timesheet-cover-scale">
        <span>{clock(windowStart)}</span>
        <span>{clock(windowEnd)}</span>
      </div>
      {coverage.bands.length === 0 ? (
        <p className="muted">{t('staff.overlapNone')}</p>
      ) : (
        <ul className="timesheet-cover-bands">
          {coverage.bands.map((band) => (
            <li key={`${band.start}-${band.end}`}>
              <span className="timesheet-cover-when">
                {clock(band.start)}–{clock(band.end)}
              </span>
              <span>{band.names.join(', ')}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
